// frontend-react/src/schemas/product.schema.js
/**
 * Schemas Zod pour la validation des produits (frontend)
 */

import { z } from 'zod';

// Schema pour la création d'un produit
export const productCreateSchema = z.object({
  sku: z
    .string()
    .max(50, 'SKU trop long (max 50 caractères)')
    .optional()
    .nullable()
    .or(z.literal(''))
    .transform((v) => (v && v.trim() ? v.trim().toUpperCase() : null)),

  name: z
    .string()
    .min(1, 'Le nom du produit est obligatoire')
    .max(255, 'Nom trop long (max 255 caractères)')
    .transform((v) => v.trim()),

  unit_price: z
    .number({ invalid_type_error: 'Le prix doit être un nombre' })
    .positive('Le prix doit être supérieur à 0')
    .finite('Prix invalide'),

  stock_quantity: z
    .number({ invalid_type_error: 'La quantité doit être un nombre' })
    .int('La quantité doit être un entier')
    .nonnegative('La quantité ne peut pas être négative')
    .default(0),

  stock_min: z
    .number({ invalid_type_error: 'Le seuil minimum doit être un nombre' })
    .int('Le seuil minimum doit être un entier')
    .nonnegative('Le seuil minimum ne peut pas être négatif')
    .default(0),
});

// Schema pour la mise à jour d'un produit
export const productUpdateSchema = z.object({
  unit_price: z
    .number({ invalid_type_error: 'Le prix doit être un nombre' })
    .nonnegative('Le prix ne peut pas être négatif')
    .finite('Prix invalide'),

  stock_min: z
    .number({ invalid_type_error: 'Le seuil minimum doit être un nombre' })
    .int('Le seuil minimum doit être un entier')
    .nonnegative('Le seuil minimum ne peut pas être négatif'),
});

// Schema pour l'ajustement de stock
export const stockAdjustmentSchema = z.object({
  delta: z
    .number({ invalid_type_error: 'Le delta doit être un nombre' })
    .int('Le delta doit être un entier')
    .refine((v) => v !== 0, 'Le delta ne peut pas être 0'),

  reason: z
    .string()
    .max(255, 'Motif trop long')
    .default('adjustment')
    .transform((v) => v.trim()),
});

// Fonctions de validation (safe)
export function validateProductCreate(data) {
  return productCreateSchema.safeParse(data);
}

export function validateProductUpdate(data) {
  return productUpdateSchema.safeParse(data);
}

export function validateStockAdjustment(data) {
  return stockAdjustmentSchema.safeParse(data);
}

// Helper pour extraire les erreurs par champ
export function getFieldErrors(result) {
  if (result.success) return {};

  const errors = {};
  for (const issue of result.error.issues) {
    const path = issue.path.join('.');
    if (!errors[path]) {
      errors[path] = issue.message;
    }
  }
  return errors;
}
